/**
 * Record import — the `import` action button's endpoints (/api/record-import).
 *
 * The same pipeline as the Assistant's file import, without the AI: the file
 * is staged, its columns are matched to the object's fields by name, the
 * person fixes the map, and the prepared records open in the shared
 * ImportPreview. Every call names the object AND the button; the server reads
 * what the button may do from the model.
 *
 * The shapes and the window are shared with the downloaded runtime and J2EE
 * (`@twasta/page-runtime/txn/capture`); this is the platform's transport.
 */
import api from './client'
import type {
  ImportLearnBody, ImportLearnResult, ImportPreviewData, ImportProfileEdit, ImportProfilesList,
  ImportRuleEdit, ImportRuleSaveResult, ImportRulesList,
  ImportSenderProfile, RecordImportInspect, RecordImportLocked, RecordImportPrepareOptions,
  ImportSenderPick, ImportSenderSearch, RecordImportReadBody, RecordImportReview,
  RecordImportSummary, RecordImportTarget, RecordImportTransport,
} from '@twasta/page-runtime/txn/capture'
import { importErrorInfo } from '@twasta/page-runtime/txn/capture'

export type {
  ImportMode, ImportColumnProfile, ImportTargetField, ImportTargetForm, ImportKeyCandidate,
  RecordImportInspect, RecordImportTarget, RecordImportPrepareOptions, RecordImportSummary,
} from '@twasta/page-runtime/txn/capture'

export async function recordImportUpload(
  t: RecordImportTarget, file: File,
  opts?: { sender?: string; review?: boolean; useAi?: boolean; generic?: boolean },
): Promise<RecordImportInspect | RecordImportLocked | RecordImportReview> {
  const form = new FormData()
  form.append('project', t.project)
  form.append('obj_name', t.objName)
  form.append('action', t.action)
  form.append('file', file)
  if (opts?.sender) form.append('sender', opts.sender)
  if (opts?.review) form.append('review', 'true')
  if (opts?.useAi === false) form.append('use_ai', 'false')
  if (opts?.generic) form.append('generic', 'true')
  const res = await api.post('/record-import/upload', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  return res.data as RecordImportInspect | RecordImportLocked | RecordImportReview
}

/** Open a password-protected PDF with the typed password and read it — or,
 *  with `review`, answer with its review step. */
export async function recordImportUnlock(
  t: RecordImportTarget, importId: string,
  body: { password: string; remember: boolean; sender?: string; review?: boolean;
          useAi?: boolean; generic?: boolean },
): Promise<RecordImportInspect | RecordImportReview> {
  const { useAi, ...rest } = body
  const res = await api.post(`/record-import/${encodeURIComponent(importId)}/unlock`, {
    project: t.project, obj_name: t.objName, action: t.action, ...rest,
    use_ai: useAi !== false,
  })
  return res.data as RecordImportInspect | RecordImportReview
}

/** The review step again, with the sender / type the person picked. */
export async function recordImportReview(
  t: RecordImportTarget, importId: string,
  body: { sender?: string; senderRef?: ImportSenderPick; docType?: string; useAi?: boolean },
): Promise<RecordImportReview> {
  const res = await api.post(`/record-import/${encodeURIComponent(importId)}/review`, {
    project: t.project, obj_name: t.objName, action: t.action,
    sender: body.sender || '', doc_type: body.docType || '', use_ai: body.useAi !== false,
    ...(body.senderRef ? { sender_ref: body.senderRef } : {}),
  })
  return res.data as RecordImportReview
}

/** Who sent a document, by name (the review step's picker). */
export async function recordImportSearchSenders(
  t: RecordImportTarget, q: string,
): Promise<ImportSenderSearch> {
  const res = await api.get('/record-import/senders/search', {
    params: { project: t.project, obj_name: t.objName, action: t.action, q },
  })
  return res.data as ImportSenderSearch
}

/** Remember this person's "Recognise the sender and document type" for the screen. */
export async function recordImportSetPreferences(
  t: RecordImportTarget, body: { detect: boolean },
): Promise<unknown> {
  const res = await api.put('/record-import/preferences', {
    project: t.project, obj_name: t.objName, action: t.action, ...body,
  })
  return res.data
}

/** Process the reviewed document (answers like an upload). */
export async function recordImportRead(
  t: RecordImportTarget, importId: string, body: RecordImportReadBody,
): Promise<RecordImportInspect> {
  const res = await api.post(`/record-import/${encodeURIComponent(importId)}/read`, {
    project: t.project, obj_name: t.objName, action: t.action,
    sender: body.sender || '', doc_type: body.docType || '',
    ...(body.prompt !== undefined ? { prompt: body.prompt } : {}),
    save_prompt: !!body.savePrompt, generic: !!body.generic,
  })
  return res.data as RecordImportInspect
}

/** The screen's keyword registry (managers only). */
export async function recordImportRules(t: RecordImportTarget): Promise<ImportRulesList> {
  const res = await api.get('/record-import/rules/list', {
    params: { project: t.project, obj_name: t.objName, action: t.action },
  })
  return res.data as ImportRulesList
}

export async function recordImportSaveRule(
  t: RecordImportTarget, edit: ImportRuleEdit,
): Promise<ImportRuleSaveResult> {
  const res = await api.put('/record-import/rules', {
    project: t.project, obj_name: t.objName, action: t.action, ...edit,
  })
  return res.data as ImportRuleSaveResult
}

export async function recordImportSetRuleStatus(
  t: RecordImportTarget,
  body: { scope: string; sender_code: string; doc_type: string; status: 'active' | 'draft' },
): Promise<unknown> {
  const res = await api.put('/record-import/rules/status', {
    project: t.project, obj_name: t.objName, action: t.action, ...body,
  })
  return res.data
}

export async function recordImportDeleteRule(
  t: RecordImportTarget, body: { scope: string; sender_code: string; doc_type: string },
): Promise<unknown> {
  const res = await api.delete('/record-import/rules', {
    params: { project: t.project, obj_name: t.objName, action: t.action, ...body },
  })
  return res.data
}

/** Store (encrypted) or remove ("") a sender's PDF password. */
export async function recordImportSetProfilePassword(
  t: RecordImportTarget, body: { sender: string; file_kind: string; password: string },
): Promise<{ has_password: boolean }> {
  const res = await api.put('/record-import/profiles/password', {
    project: t.project, obj_name: t.objName, action: t.action, ...body,
  })
  return res.data as { has_password: boolean }
}

export async function recordImportInspect(
  t: RecordImportTarget, importId: string, sheet: string,
): Promise<RecordImportInspect> {
  const res = await api.get(`/record-import/${encodeURIComponent(importId)}/inspect`, {
    params: { project: t.project, obj_name: t.objName, action: t.action, sheet },
  })
  return res.data as RecordImportInspect
}

export async function recordImportPrepare(
  t: RecordImportTarget, importId: string, opts: RecordImportPrepareOptions,
): Promise<RecordImportSummary> {
  const res = await api.post(`/record-import/${encodeURIComponent(importId)}/prepare`, {
    project: t.project,
    obj_name: t.objName,
    action: t.action,
    map: opts.map,
    mode: opts.mode,
    group_by: opts.groupBy || null,
    month_first: !!opts.monthFirst,
    sheet: opts.sheet || null,
    resolve_hints: opts.resolveHints || {},
  })
  return res.data as RecordImportSummary
}

export async function recordImportPreview(t: RecordImportTarget, importId: string): Promise<ImportPreviewData> {
  const res = await api.get(`/record-import/${encodeURIComponent(importId)}`, {
    params: { project: t.project, obj_name: t.objName, action: t.action },
  })
  return res.data as ImportPreviewData
}

/** The uploaded document itself (the preview's "File" view). */
export async function recordImportFile(t: RecordImportTarget, importId: string, page?: number): Promise<Blob> {
  const res = await api.get(`/record-import/${encodeURIComponent(importId)}/file`, {
    params: { project: t.project, obj_name: t.objName, action: t.action, ...(page ? { page } : {}) },
    responseType: 'blob',
  })
  return res.data as Blob
}

/** Remember the matches kept or picked in a saved import. */
export async function recordImportLearn(
  t: RecordImportTarget, importId: string, body: ImportLearnBody,
): Promise<ImportLearnResult> {
  const res = await api.post(`/record-import/${encodeURIComponent(importId)}/learn`, {
    project: t.project, obj_name: t.objName, action: t.action, ...body,
  })
  return res.data as ImportLearnResult
}

/** The target screen's sender profiles (and whether this person may manage them). */
export async function recordImportProfiles(t: RecordImportTarget): Promise<ImportProfilesList> {
  const res = await api.get('/record-import/profiles/list', {
    params: { project: t.project, obj_name: t.objName, action: t.action },
  })
  return res.data as ImportProfilesList
}

export async function recordImportSaveProfile(
  t: RecordImportTarget, edit: ImportProfileEdit,
): Promise<ImportSenderProfile> {
  const res = await api.put('/record-import/profiles', {
    project: t.project, obj_name: t.objName, action: t.action, ...edit,
  })
  return (res.data?.profile ?? res.data) as ImportSenderProfile
}

export async function recordImportDeleteProfile(
  t: RecordImportTarget, senderCode: string, fileKind: string, docType?: string,
): Promise<unknown> {
  const res = await api.delete('/record-import/profiles', {
    params: { project: t.project, obj_name: t.objName, action: t.action,
              sender_code: senderCode, file_kind: fileKind, doc_type: docType || '' },
  })
  return res.data
}

/** The platform's transport for the shared ImportActionWindow. */
export const platformRecordImportTransport: RecordImportTransport = {
  upload: recordImportUpload,
  inspect: recordImportInspect,
  prepare: recordImportPrepare,
  preview: recordImportPreview,
  file: recordImportFile,
  learn: recordImportLearn,
  listProfiles: recordImportProfiles,
  saveProfile: recordImportSaveProfile,
  deleteProfile: recordImportDeleteProfile,
  unlock: recordImportUnlock,
  review: recordImportReview,
  read: recordImportRead,
  searchSenders: recordImportSearchSenders,
  setPreferences: recordImportSetPreferences,
  setProfilePassword: recordImportSetProfilePassword,
  listRules: recordImportRules,
  saveRule: recordImportSaveRule,
  setRuleStatus: recordImportSetRuleStatus,
  deleteRule: recordImportDeleteRule,
  describeError: importErrorInfo,
}
